import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { space, height } from 'styled-system';
import { useRouter } from 'next/compat/router';

import { Link, ModalContainer } from 'components/core';
import { StyledPlacesAutocompleteInput } from 'src/components/form-inputs/components';

import useTranslation from 'hooks/use-translation';
import usePaths from 'hooks/use-paths';

const SearchLocation = forwardRef(({ logoImage, onChangeAddress, containerProps }, ref) => {
  const { t, Trans } = useTranslation();
  const router = useRouter();
  const { asPath } = router;
  const { path } = router?.query;
  const pathWithoutQuery = asPath.split('?')[0];
  const { href: browseHref, route: browseRoute } = usePaths({ locations: true, browse: true });
  const searchByDeliveryArea = router?.query?.searchMethod === 'deliveryArea';

  return (
    <ModalContainer ref={ref} width={535} {...containerProps}>
      <ModalContent>
        <Logo src={logoImage} height={112} alt='Store Logo' />

        <WelcomeText mt='30px' mb='20px'>
          <Trans i18nKey='chain.welcome'>
            Welcome to our <span>Online Ordering</span> Menu
          </Trans>
        </WelcomeText>

        <AddressInstructions mb='30px'>
          {searchByDeliveryArea ? (
            <Trans i18nKey='chain.enterAddress'>Enter your address to begin shopping.</Trans>
          ) : (
            <Trans i18nKey='chain.enterAddress'>Enter your address to find our nearest location.</Trans>
          )}
        </AddressInstructions>

        <AutocompleteContainer>
          <StyledPlacesAutocompleteInput
            label={t('chain.enter', 'Enter your address')}
            variant='outlined'
            onChange={onChangeAddress}
            labelstyles={labelstyles}
          />
        </AutocompleteContainer>
        {!searchByDeliveryArea && (
          <Link href={browseHref} route={browseRoute} query={{ backTo: pathWithoutQuery, path }}>
            {/* eslint-disable-next-line styled-components-a11y/anchor-is-valid */}
            <BrowseLocationsLink>{t('chain.browse', 'Browse all locations')}</BrowseLocationsLink>
          </Link>
        )}
      </ModalContent>
    </ModalContainer>
  );
});
export default SearchLocation;

const Logo = styled.img`
  ${space}
  ${height}
  width: auto;
`;

const WelcomeText = styled.div`
  color: ${({ theme }) => theme.colors.v2TextColor2};
  font-size: 21px;

  > span {
    font-weight: bold;
  }

  ${space}
`;

const AddressInstructions = styled.div`
  color: ${({ theme }) => theme.colors.v2TextColor1};
  font-size: 13px;
  ${space}
`;

const BrowseLocationsLink = styled.a`
  color: ${({ theme }) => theme.customized.colors.buttonsLinks};
  font-size: 12px;
  margin-top: 30px;
`;

const AutocompleteContainer = styled.div`
  width: 300px;
  position: relative;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 10px;
  padding-bottom: 20px;
`;

const labelstyles = css`
  &.MuiInputLabel-outlined.MuiInputLabel-shrink {
    transform: translate(12px, 8px) scale(0.75);
  }
`;
